/**
 * Deleting a record that the firm has to keep.
 *
 * Matters, clients and leads are all deleted the same way and for the same reason:
 * the record should never have existed. A duplicate, a test, an enquiry entered
 * against the wrong name.
 *
 * Three things this dialog insists on, all of which the server insists on too --
 * it is a courtesy here and a rule there:
 *
 * **A reason.** It goes on the audit record permanently. "Who deleted this and why"
 * is a question a firm gets asked years later, by a regulator or by the client.
 *
 * **Saying what actually happens.** Nothing is erased. The row, its history and its
 * documents survive; it stops appearing and stops being workable. Calling that
 * "delete" is fine, but a person about to press the button should know which one it
 * is -- the difference matters enormously if they are trying to honour an erasure
 * request, where this is the wrong tool entirely.
 *
 * **Naming the record.** The reference is in the title, so somebody who opened two
 * tabs cannot delete the wrong file.
 */

import {
  Form,
  FormActions,
  FormTextarea,
  Modal,
  useZodForm,
} from '@legalcrm/ui';
import { z } from 'zod';

const schema = z.object({
  reason: z
    .string()
    .trim()
    .min(3, 'Say why this is being deleted. It stays on the record permanently.'),
});

type Values = z.infer<typeof schema>;

export interface DeleteWithReasonDialogProps {
  open: boolean;
  onClose: () => void;
  /** What is being deleted, named the way the reader sees it: "M-2026-00004". */
  label: string;
  /** "matter", "client", "lead" -- used in the sentences below. */
  noun: string;
  /** Anything the reader should know before pressing, beyond the standard wording. */
  caveat?: string;
  busy?: boolean;
  error?: unknown;
  onConfirm: (reason: string) => Promise<unknown>;
}

export function DeleteWithReasonDialog({
  open,
  onClose,
  label,
  noun,
  caveat,
  busy,
  error,
  onConfirm,
}: DeleteWithReasonDialogProps) {
  const form = useZodForm(schema, { defaultValues: { reason: '' } });

  return (
    <Modal open={open} onClose={onClose} title={`Delete ${label}?`}>
      <Form
        form={form}
        ariaLabel={`Delete this ${noun}`}
        error={error}
        onSubmit={async (values: Values) => {
          await onConfirm(values.reason.trim());
          form.reset();
        }}
      >
        <p className="text-sm text-ink-muted">
          It stops appearing in lists and can no longer be worked on. Nothing is erased:
          the {noun}, its history and everything filed against it stay on record, along
          with your name and the reason below.
        </p>

        {caveat && <p className="text-sm text-ink-muted">{caveat}</p>}

        <FormTextarea
          form={form}
          name="reason"
          label="Why is this being deleted"
          rows={3}
          autoFocus
          hint="A duplicate, a test record, or one raised against the wrong name."
        />

        <FormActions
          submitLabel={`Delete the ${noun}`}
          destructive
          busy={busy}
          onCancel={onClose}
        />
      </Form>
    </Modal>
  );
}
